import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, updateDoc, deleteDoc, setDoc, query, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

export default function UserManagement() {
  const { userRole, orgId } = useAuth();
  const isMobile = useIsMobile();
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('viewer');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!orgId) return;
    const unsubscribe = onSnapshot(
      query(collection(db, 'users'), where('orgId', '==', orgId)),
      snap => setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsubscribe;
  }, [orgId]);

  if (userRole !== 'admin') {
    return (
      <div style={styles.denied}>
        <div style={styles.deniedIcon}>🔒</div>
        <p style={styles.deniedTitle}>Admin access required</p>
        <p style={styles.deniedSubtitle}>Contact your administrator to manage users</p>
      </div>
    );
  }

  const handleAddUser = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) { setError('All fields are required.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', result.user.uid), {
        name: name.trim(), email: email.trim(), role, orgId, createdAt: new Date(),
      });
      setSuccess(`${name} has been added successfully.`);
      setName(''); setEmail(''); setPassword(''); setRole('viewer');
      setTimeout(() => { setSuccess(''); setShowAdd(false); }, 2000);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') setError('This email is already registered.');
      else setError('Failed to create user. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    await updateDoc(doc(db, 'users', userId), { role: newRole });
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from your organization?`)) return;
    await deleteDoc(doc(db, 'users', userId));
  };

  const roleBadge = (role) => {
    const map = {
      admin:   { bg: '#eff6ff', color: '#3b82f6', border: '#bfdbfe' },
      manager: { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
      viewer:  { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
    };
    return map[role] || map.viewer;
  };

  // ── Mobile user card ─────────────────────────────────────────────────────────
  const MobileUserCard = ({ user }) => {
    const badge = roleBadge(user.role);
    return (
      <div style={m.card}>
        <div style={m.cardTopRow}>
          <div style={m.userRow}>
            <div style={m.avatar}>{user.name?.[0]?.toUpperCase() || '?'}</div>
            <div style={{ minWidth: 0 }}>
              <p style={m.userName}>{user.name}</p>
              <p style={m.userEmail}>{user.email}</p>
            </div>
          </div>
        </div>
        <div style={m.cardFooter}>
          <select
            value={user.role}
            onChange={e => handleRoleChange(user.id, e.target.value)}
            style={{ ...m.roleSelect, backgroundColor: badge.bg, color: badge.color, borderColor: badge.border }}
          >
            <option value="viewer">Viewer</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
          <button onClick={() => handleDelete(user.id, user.name)} style={m.deleteBtn}>
            Remove
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ ...styles.header, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ ...styles.title, fontSize: isMobile ? '20px' : '24px' }}>User Management</h1>
          <p style={styles.subtitle}>{users.length} team member{users.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          style={showAdd ? styles.cancelBtn : styles.addBtn}
        >
          {showAdd ? '✕ Cancel' : '+ Add User'}
        </button>
      </div>

      {/* Add User Form */}
      {showAdd && (
        <div style={{ ...styles.formCard, padding: isMobile ? '16px' : '24px', marginBottom: '16px' }}>
          <h3 style={styles.formTitle}>Add Team Member</h3>
          <p style={styles.formSubtitle}>They'll receive login credentials to access StockSpace</p>

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.successMsg}>{success}</div>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px' }}>
            {[
              { label: 'Full Name', value: name, set: setName, placeholder: 'e.g. Rahul Sharma' },
              { label: 'Email Address', value: email, set: setEmail, placeholder: 'rahul@example.com', type: 'email' },
              { label: 'Password', value: password, set: setPassword, placeholder: 'Min 6 characters', type: 'password' },
            ].map(field => (
              <div key={field.label}>
                <label style={styles.label}>{field.label}</label>
                <input
                  type={field.type || 'text'}
                  value={field.value}
                  onChange={e => field.set(e.target.value)}
                  placeholder={field.placeholder}
                  style={{ ...styles.input, fontSize: '16px' /* prevents iOS zoom */ }}
                />
              </div>
            ))}
            <div>
              <label style={styles.label}>Role</label>
              <select value={role} onChange={e => setRole(e.target.value)} style={{ ...styles.input, fontSize: '16px' }}>
                <option value="viewer">Viewer — read only</option>
                <option value="manager">Manager — update stock</option>
                <option value="admin">Admin — full access</option>
              </select>
            </div>
          </div>
          <button onClick={handleAddUser} disabled={loading} style={{ ...(loading ? styles.btnDisabled : styles.btn), width: isMobile ? '100%' : 'auto' }}>
            {loading ? 'Creating account...' : 'Create User'}
          </button>
        </div>
      )}

      {/* Role info cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '10px', marginBottom: '20px',
      }}>
        {[
          { role: 'Admin',   desc: 'Full access — manage products, users, and all data', color: '#3b82f6', bg: '#eff6ff' },
          { role: 'Manager', desc: 'Can update stock levels and view all reports',        color: '#16a34a', bg: '#f0fdf4' },
          { role: 'Viewer',  desc: 'Read-only access to stock and reports',               color: '#64748b', bg: '#f8fafc' },
        ].map(r => (
          <div key={r.role} style={{
            ...styles.roleCard, backgroundColor: r.bg, borderColor: r.color + '30',
            ...(isMobile ? { display: 'flex', alignItems: 'center', gap: '12px' } : {}),
          }}>
            <p style={{ ...styles.roleCardTitle, color: r.color, marginBottom: isMobile ? 0 : '4px' }}>{r.role}</p>
            <p style={styles.roleCardDesc}>{r.desc}</p>
          </div>
        ))}
      </div>

      {/* Mobile: cards / Desktop: table */}
      {isMobile ? (
        <div>
          {users.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              No users found
            </div>
          ) : (
            users.map(user => <MobileUserCard key={user.id} user={user} />)
          )}
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Name', 'Email', 'Role', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const badge = roleBadge(user.role);
                return (
                  <tr key={user.id} style={styles.tr} className="table-row-hover">
                    <td style={styles.tdName}>
                      <div style={styles.userRow}>
                        <div style={styles.avatar}>{user.name?.[0]?.toUpperCase() || '?'}</div>
                        {user.name}
                      </div>
                    </td>
                    <td style={{ ...styles.td, color: '#64748b' }}>{user.email}</td>
                    <td style={styles.td}>
                      <select
                        value={user.role}
                        onChange={e => handleRoleChange(user.id, e.target.value)}
                        style={{ ...styles.roleSelect, backgroundColor: badge.bg, color: badge.color, borderColor: badge.border }}
                      >
                        <option value="viewer">Viewer</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => handleDelete(user.id, user.name)} style={styles.deleteBtn}>Remove</button>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Mobile styles ─────────────────────────────────────────────────────────────
const m = {
  card: {
    backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
    borderRadius: '12px', padding: '14px', marginBottom: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardTopRow: { marginBottom: '12px' },
  userRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: {
    width: '38px', height: '38px', borderRadius: '50%',
    backgroundColor: '#eff6ff', color: '#3b82f6',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '15px', fontWeight: '700', flexShrink: 0,
  },
  userName: { fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '2px' },
  userEmail: { fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
  roleSelect: {
    padding: '6px 12px', borderRadius: '20px', border: '1.5px solid',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer', outline: 'none',
  },
  deleteBtn: {
    padding: '7px 16px', backgroundColor: 'transparent',
    border: '1.5px solid #fecaca', color: '#ef4444',
    borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: '500',
  },
};

// ── Desktop styles (unchanged) ────────────────────────────────────────────────
const styles = {
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: '#64748b' },
  addBtn: { padding: '9px 20px', backgroundColor: '#3b82f6', border: 'none', color: '#ffffff', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)' },
  cancelBtn: { padding: '9px 20px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#64748b', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  formCard: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '24px', marginBottom: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  formTitle: { fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  formSubtitle: { fontSize: '13px', color: '#64748b', marginBottom: '20px' },
  field: {},
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '9px 12px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', boxSizing: 'border-box' },
  btn: { padding: '10px 24px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)' },
  btnDisabled: { padding: '10px 24px', backgroundColor: '#93c5fd', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'not-allowed' },
  error: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  successMsg: { backgroundColor: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  roleCard: { padding: '14px 16px', borderRadius: '10px', border: '1px solid' },
  roleCardTitle: { fontSize: '13px', fontWeight: '700', marginBottom: '4px' },
  roleCardDesc: { fontSize: '12px', color: '#64748b', lineHeight: 1.4 },
  tableWrapper: { backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '13px 16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#f8fafc', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'all 0.15s ease' },
  tdName: { padding: '13px 16px', color: '#1e293b', fontSize: '14px', fontWeight: '500' },
  td: { padding: '13px 16px', color: '#374151', fontSize: '14px' },
  userRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', flexShrink: 0 },
  roleSelect: { padding: '5px 12px', borderRadius: '20px', border: '1.5px solid', fontSize: '13px', fontWeight: '600', cursor: 'pointer', outline: 'none' },
  deleteBtn: { padding: '6px 14px', backgroundColor: 'transparent', border: '1.5px solid #fecaca', color: '#ef4444', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' },
  denied: { textAlign: 'center', padding: '80px 24px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  deniedIcon: { fontSize: '48px', marginBottom: '16px' },
  deniedTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' },
  deniedSubtitle: { fontSize: '14px', color: '#64748b' },
};